import {Component, OnInit} from '@angular/core';
import {Station, StationMap, StationItem} from "../../models/station";
import {StationService} from "../../services/station.service";
import {AttachmentPointFilter} from "../../models/filters/attachmentpoints-filter";
import {environment} from "../../../environments/environment";
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {StationDeleteComponent} from "../station-delete/station-delete.component";
import {StationFormComponent} from "../station-form/station-form.component";
import { fadeInAndOut } from '../../utils/animations-utils';
import { ToastsManager } from 'ng2-toastr';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-stations',
  animations: [fadeInAndOut()],
  templateUrl: './stations-list.component.html',
  styleUrls: ['./stations-list.component.css']
})
export class StationsListComponent implements OnInit {

  p: number = 1;      // current page
  total: number;      // total count of records
  itemsPerPage = environment.itemPerPage;       // number of records to display on each page
  listLoading : boolean;
  downloading : boolean = false;
  
  /**
   * Keys : string criterias
   * Values : boolean => true if ascending, false otherwise
   */
  sortingCriterias: Map<string, boolean> = new Map();
  filters: Map<string, string>;


  /** Data for modals */
  stationToModify: Station;
  stationToDelete: Station;

  deleteModal: NgbModalRef;
  formModal: NgbModalRef;

  /** Station to display on the table */
  stations: StationItem[] = [];

  /** Selected stations */
  selectedStations: Map<StationItem, boolean> = new Map();
  selectAllChecked: boolean = false;

  /* sort and filters */
  NAME_FILTER = "station.name";
  GRP_FILTER = "grp.name";
  AP_FILTER = "numberOfAttachments";
  TYPE_FILTER = "station.type";
  DISABLED_FILTER = "station.disabled";
  listFiltered = false;


  constructor(private stationService: StationService,
              private modalService: NgbModal,
              public toast : ToastsManager,
              public translate : TranslateService) {
  }

  ngOnInit() {
    this.initFilters();

    if (localStorage.getItem("itemsPerPage")) {
      this.itemsPerPage = parseInt(localStorage.getItem("itemsPerPage"))
    }
    this.getPage(1);
  }

  getPage(page: number) {
    this.p = page;
    this.listLoading = true;
    this.stationService.getPaginatedFilteredSortedStations(this.p, this.itemsPerPage, this.filters, this.sortingCriterias).subscribe(data => {
      this.stations = data.res;
      this.total = data.headers.get("x-total-count");
      this.listLoading = false;
    });
  }

  /**
   * Sorting system function as follow
   * On first click on a criteria => add the criteria as ascending
   * On second click => put the critiera to descending
   * On third click => remove the criteria from the map
   */
  updateSortingCriterias(criteria: string) {
    if (this.sortingCriterias.has(criteria)) {
      if (this.sortingCriterias.get(criteria)) {   // criteria is present as true => switch to false
        this.sortingCriterias.set(criteria, !this.sortingCriterias.get(criteria));
      } else {    // criteria present as false => delete it
        this.sortingCriterias.delete(criteria);
      }
    } else {
      this.sortingCriterias.set(criteria, true);
    }
  }

  // ---------------- Filters ----------------------
  changeItemCount(value) {
    this.itemsPerPage = value;
    this.reloadList();

    localStorage.setItem("itemsPerPage", value);
  }

  initFilters() {
    this.filters = new Map();
    this.filters[this.TYPE_FILTER] = "";
  }

  /**
   * Create a new empty map
   * Retrieve first page
   */
  clearFilters() {
    this.listFiltered = false;
    this.initFilters()
    this.reloadList();
  }

  onTypeChange() {
    this.reloadList();
  }


  sortBy(criteria: string) {
    this.updateSortingCriterias(criteria);
    this.reloadList();
  }

  /**
   * Reload lists according to filters, sorting criterias from the first page
   */
  reloadList() {
    this.listFiltered = false;
    Object.values(this.filters).forEach(value => {
      if (value) {
        this.listFiltered = true;
      }
    });
    this.getPage(1);
  }

  onUpdateClick(station: StationItem): void {
    this.stationService.get(station.id).subscribe(res => {
      this.formModal = this.modalService.open(StationFormComponent, {size: "lg", backdrop: "static"});
      this.formModal.componentInstance.creationMode = false;
      this.formModal.componentInstance.station = res;
      this.formModal.componentInstance.onUpdateValidation.subscribe(resultStation => {
        this.reloadList();
      });
    });
  }

  /**
   * On new account creation display a modal form
   */
  onNewClick(): void {
    this.formModal = this.modalService.open(StationFormComponent, {size: "lg", backdrop: "static"});
    this.formModal.componentInstance.creationMode = true;
    this.formModal.componentInstance.station = {} as StationMap;
    this.formModal.componentInstance.onCreateValidation.subscribe(() => this.getPage(this.p));
  }

  onDeleteClick(s : StationItem) {
    this.deleteModal = this.modalService.open(StationDeleteComponent, {centered: true});
    this.deleteModal.componentInstance.station = s;
    this.deleteModal.componentInstance.onDeleteValidation.subscribe(() => {
      this.getPage(this.p);
      this.toast.success(this.translate.instant("toast.stationdisabled"), this.translate.instant("toast.success"));
    });

  }

  /**
   * Trigerred when an account has been updated through account form modal
   * Update the information in the array
   */
  stationHasBeenUpdated(updatedStation: StationMap) {
    this.getPage(this.p);
  }

  selectStation(s: StationItem) {
    this.selectedStations.set(s, true);
  }

  deselectStation(s: StationItem) {
    this.selectedStations.delete(s);
    this.selectAllChecked = false;
  }

  onCheckboxSelectAllStationChange() {
    if (this.selectedStations.size < this.stations.length) {
      this.stations.forEach(ap => this.selectedStations.set(ap, true));
    } else {
      this.selectedStations.clear();
    }
  }

  onCheckboxChange(s: StationItem) {
    if (!this.selectedStations.get(s)) {
      this.selectStation(s);
    } else {
      this.deselectStation(s);
    }
  }

  downloadXLSFile() {
    this.downloading = true;
    this.stationService.downloadXLSFile(
      this.p,
      this.itemsPerPage,
      this.filters,
      this.sortingCriterias)
      .subscribe(isTruncated => {
        if (isTruncated) {
          this.toast.warning(this.translate.instant('common.downloadOk'));
        } else {
          this.toast.success(this.translate.instant('common.downloadTruncated'));
        }
        this.downloading = false;
      });
  }
}



// WEBPACK FOOTER //
// ./src/app/stations/stations-list/stations-list.component.ts